import { ENROLLMENT_STATUS_LABELS, type EnrollmentStatus } from "@byfluent/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  Loading,
  Modal,
  PageHeader,
  TextInput,
} from "../../components/ui";
import { API_URL, api, errorMessage } from "../../lib/api";
import { brl, dateBR, dateTimeBR, formatBrPhone } from "../../lib/format";
import type { Quota } from "../../lib/types";
import { ENROLLMENT_COLOR } from "./AdminStudents";

interface Student360 {
  student: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    birthDate: string | null;
    cefrLevel: string | null;
    firstContactAt: string | null;
  };
  classmates: { id: string; name: string }[];
  enrollment: {
    id: string;
    status: EnrollmentStatus;
    planName: string;
    activatedAt: string | null;
    cancelAt: string | null;
    singlePayer: boolean;
    isDuo: boolean;
  };
  teacher: { id: string; name: string; email: string } | null;
  seller: { id: string; name: string } | null;
  scheduleLabel: string;
  contractors: {
    id: string;
    name: string;
    isGuardian: boolean;
    email: string | null;
    phone: string | null;
    cpfMasked: string | null;
  }[];
  subscriptions: {
    id: string;
    billingType: string;
    valueCents: number;
    status: string;
    nextDueDate: string | null;
    asaasSubscriptionId: string | null;
  }[];
  payments: {
    id: string;
    asaasPaymentId: string;
    valueCents: number;
    dueDate: string;
    status: string;
    confirmedAt: string | null;
    invoiceUrl: string | null;
  }[];
  refunds: { id: string; status: string; reason: string; createdAt: string }[];
  guarantee: { firstPaymentAt: string | null; available: boolean };
  consents: {
    id: string;
    document: string;
    version: string;
    acceptedAt: string;
    ip: string | null;
  }[];
  quota: Quota | null;
}

type Action = "refund" | "cancel" | "reassign" | "grant" | "contact" | "anonymize" | "cpf" | null;

const DOCS: Record<string, string> = {
  lessons_contract: "Contrato de aulas",
  platform_terms: "Termos da plataforma",
  privacy: "Política de privacidade",
  contact: "Contato por WhatsApp e e-mail",
};

export function AdminStudent() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["student360", id],
    queryFn: () => api.get<Student360>(`/v1/admin/students/${id}`),
  });
  const [action, setAction] = useState<Action>(null);
  const [reason, setReason] = useState("");
  const [minutes, setMinutes] = useState("30");
  const [contact, setContact] = useState({ email: "", phone: "" });
  const [cpf, setCpf] = useState<string | null>(null);
  const [contractorId, setContractorId] = useState("");
  const done = () => {
    setAction(null);
    setReason("");
    qc.invalidateQueries({ queryKey: ["student360", id] });
  };
  const run = useMutation({
    mutationFn: async (a: Exclude<Action, null>) => {
      const enrollmentId = q.data?.enrollment.id;
      switch (a) {
        case "refund":
          return api.post(`/v1/admin/enrollments/${enrollmentId}/refund`, { reason });
        case "cancel":
          return api.post(`/v1/admin/enrollments/${enrollmentId}/cancel`, { reason });
        case "reassign":
          return api.post(`/v1/admin/enrollments/${enrollmentId}/reassign`, { reason });
        case "grant":
          return api.post(`/v1/students/${id}/quota-grants`, { minutes: Number(minutes), reason });
        case "contact":
          return api.patch(`/v1/admin/students/${id}/contact`, {
            email: contact.email || undefined,
            phone: contact.phone || undefined,
            reason,
          });
        case "anonymize":
          return api.post(`/v1/admin/students/${id}/anonymize`, { reason });
        case "cpf": {
          const r = await api.post<{ cpf: string }>(`/v1/admin/students/${id}/reveal-cpf`, {
            contractorId,
            reason,
          });
          setCpf(r.cpf);
          return r;
        }
      }
    },
    onSuccess: (_r, a) => {
      if (a !== "cpf") done();
    },
  });
  const welcome = useMutation({
    mutationFn: () => api.post(`/v1/admin/enrollments/${q.data?.enrollment.id}/resend-welcome`),
  });

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert kind="risk">{errorMessage(q.error)}</Alert>;
  const d = q.data;
  const live = ["active", "overdue", "suspended"].includes(d.enrollment.status);

  const titles: Record<Exclude<Action, null>, string> = {
    refund: "Reembolsar (garantia de 7 dias)",
    cancel: "Cancelar assinatura",
    reassign: "Trocar de professor",
    grant: "Conceder minutos de IA",
    contact: "Corrigir e-mail ou telefone",
    anonymize: "Apagar dados",
    cpf: "Ver CPF completo",
  };

  return (
    <>
      <PageHeader
        title={d.student.name}
        subtitle={
          <span className="row">
            <Badge color={ENROLLMENT_COLOR[d.enrollment.status]}>
              {ENROLLMENT_STATUS_LABELS[d.enrollment.status]}
            </Badge>
            {d.enrollment.planName} · {d.scheduleLabel || "sem horário"}
          </span>
        }
        actions={<Link to="/admin/alunos">Voltar</Link>}
      />
      <Card title="Ações de suporte">
        <div className="row">
          <Button
            variant="danger"
            disabled={!d.guarantee.available}
            onClick={() => setAction("refund")}
          >
            Reembolsar (garantia)
          </Button>
          <Button
            variant="secondary"
            disabled={!live && d.enrollment.status !== "filled"}
            onClick={() => setAction("cancel")}
          >
            Cancelar assinatura
          </Button>
          <Button variant="secondary" disabled={!live} onClick={() => setAction("reassign")}>
            Trocar de professor
          </Button>
          <Button variant="secondary" disabled={!d.quota} onClick={() => setAction("grant")}>
            Conceder minutos
          </Button>
          <Button
            variant="secondary"
            disabled={d.enrollment.status !== "active"}
            loading={welcome.isPending}
            onClick={() => welcome.mutate()}
          >
            Reenviar boas-vindas
          </Button>
          <Button variant="secondary" onClick={() => setAction("contact")}>
            Corrigir contato
          </Button>
          <a
            className="btn ghost"
            href={`${API_URL}/v1/admin/students/${id}/export`}
            target="_blank"
            rel="noreferrer"
          >
            Exportar dados
          </a>
          <Button variant="ghost" onClick={() => setAction("anonymize")}>
            Apagar dados
          </Button>
        </div>
        {welcome.isSuccess ? (
          <Alert kind="success">E-mails de boas-vindas reenviados.</Alert>
        ) : null}
        {d.enrollment.cancelAt ? (
          <div style={{ marginTop: 12 }}>
            <Alert kind="warning">
              Cancelamento pedido: fica ativo até {dateBR(d.enrollment.cancelAt)}.
            </Alert>
          </div>
        ) : null}
        {!d.guarantee.available && d.guarantee.firstPaymentAt ? (
          <p className="small muted" style={{ marginTop: 8 }}>
            Garantia de 7 dias encerrada (primeiro pagamento em{" "}
            {dateTimeBR(d.guarantee.firstPaymentAt)}).
          </p>
        ) : null}
      </Card>
      <div className="grid cols-2" style={{ marginTop: 16 }}>
        <Card title="Aluno">
          <div className="stack small">
            <div>E-mail: {d.student.email}</div>
            <div>WhatsApp: {d.student.phone ? formatBrPhone(d.student.phone) : ""}</div>
            <div>Nascimento: {dateBR(d.student.birthDate)}</div>
            <div>Nível: {d.student.cefrLevel}</div>
            <div>Professor: {d.teacher?.name ?? "sem professor"}</div>
            <div>Vendedora: {d.seller?.name ?? "sem vendedora"}</div>
            <div>
              Primeiro contato:{" "}
              {d.student.firstContactAt ? dateTimeBR(d.student.firstContactAt) : "pendente"}
            </div>
            {d.classmates.length ? (
              <div>Dupla com: {d.classmates.map((c) => c.name).join(", ")}</div>
            ) : null}
            {d.quota ? (
              <div>
                IA no mês: {Math.floor(d.quota.usedMinutes)} de {d.quota.allowedMinutes} min
              </div>
            ) : null}
          </div>
        </Card>
        <Card title="Contratante">
          {d.contractors.map((c) => (
            <div key={c.id} className="stack small" style={{ marginBottom: 12 }}>
              <strong>
                {c.name} {c.isGuardian ? <Badge color="violet">Responsável</Badge> : null}
              </strong>
              <span>
                CPF {c.cpfMasked}{" "}
                <Button
                  size="small"
                  variant="ghost"
                  onClick={() => {
                    setContractorId(c.id);
                    setCpf(null);
                    setAction("cpf");
                  }}
                >
                  Ver CPF
                </Button>
              </span>
              <span>
                {c.email} {c.phone ? `· ${formatBrPhone(c.phone)}` : ""}
              </span>
            </div>
          ))}
          <h3>Aceites</h3>
          {d.consents.map((c) => (
            <div key={c.id} className="small">
              {DOCS[c.document] ?? c.document} v{c.version} · {dateTimeBR(c.acceptedAt)} · IP {c.ip}
            </div>
          ))}
        </Card>
      </div>
      <Card title="Pagamentos">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Vencimento</th>
                <th className="num">Valor</th>
                <th>Status</th>
                <th>Confirmado</th>
                <th>Fatura</th>
              </tr>
            </thead>
            <tbody>
              {d.payments.map((p) => (
                <tr key={p.id}>
                  <td>{dateBR(p.dueDate)}</td>
                  <td className="num">{brl(p.valueCents)}</td>
                  <td>
                    <Badge
                      color={
                        p.status === "CONFIRMED" || p.status === "RECEIVED"
                          ? "green"
                          : p.status === "OVERDUE"
                            ? "gold"
                            : "gray"
                      }
                    >
                      {p.status}
                    </Badge>
                  </td>
                  <td className="small">{dateTimeBR(p.confirmedAt)}</td>
                  <td>
                    {p.invoiceUrl ? (
                      <a href={p.invoiceUrl} target="_blank" rel="noreferrer">
                        Abrir
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {d.refunds.map((r) => (
          <div key={r.id} style={{ marginTop: 8 }}>
            <Alert kind={r.status === "done" ? "success" : "warning"}>
              Reembolso{" "}
              {r.status === "done"
                ? "feito"
                : r.status === "manual_required"
                  ? "pendente de procedimento manual na Asaas"
                  : r.status}{" "}
              em {dateTimeBR(r.createdAt)}: {r.reason}
            </Alert>
          </div>
        ))}
      </Card>
      {action ? (
        <Modal title={titles[action]} onClose={() => setAction(null)}>
          <div className="stack">
            {action === "refund" ? (
              <Alert kind="warning">
                Estorna o primeiro pagamento, cancela a assinatura, libera o horário e avisa aluno,
                professor e vendedora.
              </Alert>
            ) : null}
            {action === "cancel" ? (
              <Alert kind="note">
                Encerra a assinatura na Asaas; o aluno fica ativo até o fim do período pago. Sem
                multa.
              </Alert>
            ) : null}
            {action === "reassign" ? (
              <Alert kind="note">
                Roda o motor com as janelas originais do aluno, sem o professor atual. Os próximos
                pagamentos são repassados ao novo professor.
              </Alert>
            ) : null}
            {action === "anonymize" ? (
              <Alert kind="risk">
                Apaga contatos, endereço, nascimento, observações e transcrições. Ficam só os dados
                fiscais. Não dá para desfazer.
              </Alert>
            ) : null}
            {action === "grant" ? (
              <TextInput
                label="Minutos"
                type="number"
                min={1}
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
              />
            ) : null}
            {action === "contact" ? (
              <>
                <TextInput
                  label="Novo e-mail"
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                />
                <TextInput
                  label="Novo WhatsApp"
                  value={contact.phone}
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                />
              </>
            ) : null}
            {cpf ? <div className="copy-box">{cpf}</div> : null}
            <TextInput
              label="Motivo (obrigatório, fica na auditoria)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {run.error ? <Alert kind="risk">{errorMessage(run.error)}</Alert> : null}
            <Button
              variant={action === "refund" || action === "anonymize" ? "danger" : "primary"}
              loading={run.isPending}
              disabled={reason.trim().length < 5}
              onClick={() => run.mutate(action)}
            >
              Confirmar
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
